import { motion } from "framer-motion";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { usePageMeta } from "@/lib/seo";

const ARCH_IMG =
  "https://images.unsplash.com/photo-1488972685288-c3fd157d7c7a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwYXJjaGl0ZWN0dXJlJTIwYWJzdHJhY3QlMjBzdHJ1Y3R1cmUlMjBjb25zdWx0aW5nfGVufDB8fHx8MTc5MTgyNjQ4MHww&ixlib=rb-4.1.0&q=85";

const APPROACH = [
  {
    n: "I",
    title: "Explore",
    body: "We start with an open discussion: your position, your priorities and the options realistically available to the business.",
  },
  {
    n: "II",
    title: "Plan",
    body: "We help you assess those options and set out practical next steps, shaped around your circumstances and commercial goals.",
  },
  {
    n: "III",
    title: "Move forward",
    body: "We stay involved as plans are put into practice — organising information and coordinating with external advisers and service providers where needed.",
  },
];

export default function Expertise() {
  usePageMeta(
    "Expertise — W Group Consulting",
    "Helping businesses explore opportunities in different markets and jurisdictions — from the first discussion through to implementation.",
    "/expertise"
  );

  return (
    <>
      <PageHero
        eyebrow="Expertise"
        titleLines={["Exploring new", "jurisdictions."]}
        description="For businesses considering opportunities in different markets, we help you understand what needs to be explored locally, consider the commercial and operational requirements, and coordinate with relevant professional advisers where needed."
      />

      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32" data-testid="expertise-jurisdictions">
        <div className="grid items-center gap-14 lg:grid-cols-[1fr_1.1fr]">
          <Reveal>
            <div className="overflow-hidden">
              <motion.img
                src={ARCH_IMG}
                alt="Minimalist architectural structure"
                className="aspect-[4/5] w-full object-cover"
                initial={{ scale: 1.15 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
          </Reveal>
          <div>
            <Reveal>
              <h2 className="font-display text-3xl text-ink sm:text-4xl">Working across borders</h2>
              <p className="mt-8 text-base leading-relaxed text-gray-600">
                Considering a new market raises practical questions: how the
                jurisdiction works, what the commercial and operational
                requirements are, and what needs to be understood locally
                before any decision is made. We help you work through those
                questions in a structured way.
              </p>
              <p className="mt-5 text-base leading-relaxed text-gray-600">
                Where a matter calls for local legal, regulatory or other
                specialist input, we help you identify what is needed and
                coordinate with the relevant professional advisers — keeping
                the work organised and moving in one direction.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-ink py-24 text-paper lg:py-32" data-testid="expertise-approach">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="mb-4 text-xs uppercase tracking-[0.3em] text-gold">How we work</p>
            <h2 className="max-w-2xl font-display text-4xl leading-tight sm:text-5xl">
              Explore, plan, move forward.
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-px bg-paper/10 md:grid-cols-3">
            {APPROACH.map((a, i) => (
              <Reveal key={a.n} delay={i * 0.1}>
                <div className="flex h-full flex-col bg-ink p-8 lg:p-10" data-testid={`approach-${a.n}`}>
                  <span className="font-display text-3xl italic text-gold">{a.n}</span>
                  <h3 className="mt-8 font-display text-2xl">{a.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-paper/65">{a.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
